/**
 * Authentication Module
 * Manages client, provider, and admin user authentication state and role assignments.
 */

export class AuthModule {
  constructor() {
    this.currentUser = null;
    this.currentRole = null;
  }

  setCurrentUser(user, role = null) {
    this.currentUser = user;
    this.currentRole = role;
  }

  getUserRole() {
    return this.currentRole;
  }

  isAuthenticated() {
    return !!this.currentUser;
  }

  normalizeProviderState(provider = {}) {
    const normalized = { ...(provider || {}) };
    normalized.role = "provider";
    const subscriptionStatus = String(
      normalized.subscriptionStatus || normalized.accountStatus || normalized.verificationStatus || "pending"
    ).toLowerCase();
    const verificationStatus = String(normalized.verificationStatus || "").toLowerCase();
    const accountStatus = String(normalized.accountStatus || "").toLowerCase();
    const payoutStatus = String(normalized.payoutStatus || "pending").toLowerCase();

    normalized.subscriptionStatus = subscriptionStatus;
    normalized.verificationStatus = verificationStatus || (
      subscriptionStatus === "approved" || subscriptionStatus === "active" ? "verified" :
      subscriptionStatus === "rejected" ? "rejected" : "pending"
    );
    normalized.accountStatus = accountStatus || (
      subscriptionStatus === "approved" || subscriptionStatus === "active" ? "active" :
      subscriptionStatus === "rejected" ? "rejected" :
      subscriptionStatus === "suspended" ? "suspended" : "pending"
    );
    normalized.payoutStatus = payoutStatus;

    if (normalized.subscriptionStatus === "rejected") {
      normalized.accountStatus = "rejected";
      normalized.verificationStatus = "rejected";
    }
    if (normalized.subscriptionStatus === "suspended") {
      normalized.accountStatus = "suspended";
    }
    if (normalized.subscriptionStatus === "approved" || normalized.subscriptionStatus === "active" || normalized.accountStatus === "active") {
      normalized.accountStatus = "active";
      normalized.verificationStatus = "verified";
      normalized.payoutStatus = normalized.payoutStatus || "approved";
    }
    if (normalized.subscriptionStatus === "pending") {
      normalized.accountStatus = normalized.accountStatus || "pending";
      normalized.verificationStatus = normalized.verificationStatus || "pending";
      normalized.payoutStatus = normalized.payoutStatus || "pending";
    }

    return normalized;
  }

  isProviderApproved(provider) {
    const normalized = this.normalizeProviderState(provider);
    return normalized.role === "provider" &&
      ["approved", "active"].includes(normalized.subscriptionStatus) &&
      ["verified", "active"].includes(normalized.verificationStatus) &&
      ["active", "approved"].includes(normalized.accountStatus);
  }

  isControlledBetaAllowed(hostname = "", config = {}) {
    const host = String(hostname || "").toLowerCase();
    if (["localhost", "127.0.0.1", "::1"].includes(host)) {
      return { allowed: true, reason: "localhost" };
    }

    const appCheckKey = String(config.appCheckSiteKey || "").trim();
    const recaptchaKey = String(config.recaptchaSiteKey || "").trim();
    if (!appCheckKey || !recaptchaKey) {
      return {
        allowed: false,
        reason: "Public domain access is blocked until App Check and reCAPTCHA keys are configured."
      };
    }

    return { allowed: true, reason: null };
  }

  async signOut() {
    if (window.firebaseAuthService) {
      await window.firebaseAuthService.signOut();
    } else if (window.firebase && window.firebase.auth) {
      await window.firebase.auth().signOut();
    }
    this.currentUser = null;
    this.currentRole = null;
  }
}

export const authModule = new AuthModule();
